import { LOCATION_INITIALIZED } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ErrorHandler,
  Injector,
  LOCALE_ID,
  NgModule,
} from "@angular/core";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import {
  MatMomentDateModule,
  provideMomentDateAdapter,
} from "@angular/material-moment-adapter";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { ELocale } from "@core/enums/elocale.enum";
import { Language } from "@core/enums/language";
import { GlobalErrorHandler } from "@core/handlers/global-error.handler";
import { RegisterInterceptor } from "@core/interceptors/register.interceptor";
import { AuthService } from "@core/services/auth.service";
import { environment } from "@env/environment";
import { AuthEffects } from "@features/auth/store/auth.effects";
import { EmployeesEffects } from "@features/employees/store/employees.effects";
import { GroupsEffects } from "@features/groups/store/groups.effects";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";

import {
  ApiModule as AuthApiModule,
  Configuration as AuthApiConfiguration,
} from "./api/auth";
import * as fromApp from "./store/app.reducer";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";

export function HttpLoaderFactory(httpClient: HttpBackend) {
  return new MultiTranslateHttpLoader(httpClient, [
    { prefix: "./assets/i18n/features/auth/" },
    { prefix: "./assets/i18n/features/common/" },
    { prefix: "./assets/i18n/features/shared/" },
    { prefix: "./assets/i18n/features/notfound/" },
    { prefix: "./assets/i18n/features/groups/" },
    { prefix: "./assets/i18n/features/employees/" },
  ]);
}

export function authApiConfigurationFactory(): AuthApiConfiguration {
  return new AuthApiConfiguration({
    basePath: environment.AUTH_API_BASE_URL,
    // Send/receive the HttpOnly refresh-token cookie on auth requests.
    withCredentials: true,
  });
}

@NgModule({
  imports: [
    // Standalone; bootstrapped through the module until main.ts moves to
    // bootstrapApplication.
    AppComponent,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatMomentDateModule,
    StoreModule.forRoot(fromApp.APP_REDUCER),
    EffectsModule.forRoot([AuthEffects, GroupsEffects, EmployeesEffects]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    AuthApiModule.forRoot(authApiConfigurationFactory),
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService, injector: Injector): object => {
        return async (): Promise<void> => {
          await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

          // this language will be used as a fallback when a translation
          translate.setDefaultLang("nl");
          try {
            // Honour the language picked on the login screen (or in the sidenav)
            // so a reload does not fall back to the default language.
            const storedLanguage = localStorage.getItem(
              ELocale.localStorageKey
            );
            const language =
              storedLanguage &&
              Object.values<string>(Language).includes(storedLanguage)
                ? storedLanguage
                : translate.defaultLang;
            await translate.use(language).toPromise();
          } catch (err) {
            console.log("Error loading translations:", err);
          }
        };
      },
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      // Attempt a silent refresh before routing starts, so a valid session (HttpOnly refresh
      // cookie) survives a full page reload even though the access token lives only in memory.
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService): (() => Promise<void>) => {
        return (): Promise<void> => authService.bootstrapSession();
      },
      deps: [AuthService],
      multi: true,
    },

    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "DD-MM-YYYY",
        },
        display: {
          dateInput: "DD-MM-YYYY",
          monthYearLabel: "MMM YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "MMMM YYYY",
        },
      },
    },
    provideMomentDateAdapter(),
    {
      provide: LOCALE_ID,
      useValue: "nl-NL",
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RegisterInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: "dialog",
        restoreFocus: true,
        // Every dialog gets the design system's dialog layout: title and
        // actions pinned, the content the only part that scrolls. Without it
        // the surface also stays at Material's 80% opening scale, which is
        // why the group and employee dialogs looked small and cramped.
        panelClass: "talero-dialog",
        maxWidth: "92vw",
        maxHeight: "92vh",
      },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
